/**
 * UNIFIED TRUST LAYER
 *
 * A single source of truth for the trust signals a user (freelancer,
 * applicant, client) carries across the platform. This replaces the
 * ad-hoc "Verified" / "Top Rated" / "Rising" chips that were copy/pasted
 * across freelancer cards, proposals, and job applications.
 *
 * The existing verification flows (`verification-pages.js`, `vetting-page.js`,
 * admin KYC in `admin-phase4-5-pages.js`) still own the underlying data;
 * this component only renders the final derived signals on top of whatever
 * the backend sends back on a user object.
 *
 * Supported signals (per the product spec):
 *   - id_verified       (KYC / government ID check passed)
 *   - payment_verified  (billing or payout method verified)
 *   - top_rated         (platform-awarded quality tier)
 *   - response_rate     (derived from messages/proposals)
 *   - on_time_delivery  (derived from contract milestones)
 *
 * Usage:
 *   <TrustBadge type="id_verified" />
 *   <TrustBadgeGroup user={freelancer} />
 *   <TrustScore user={freelancer} />   // the single aggregate number
 */
import React from 'react';
import {
  IconShieldCheck,
  IconCreditCard,
  IconStarFilled,
  IconBolt,
  IconClock,
  IconUserCheck,
  IconSparkles,
  IconAward,
} from '../../utils/icon-compat';

// ---------------------------------------------------------------------------
// Signal definitions
// ---------------------------------------------------------------------------

/**
 * Each signal has a canonical definition so every surface (card, proposal,
 * profile header, admin queue) renders it the same way.
 */
export const TRUST_SIGNALS = {
  id_verified: {
    icon: IconShieldCheck,
    label: 'ID Verified',
    shortLabel: 'ID',
    tooltip: 'Government ID verified via KYC',
    className: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    weight: 25,
  },
  payment_verified: {
    icon: IconCreditCard,
    label: 'Payment Verified',
    shortLabel: 'Payment',
    tooltip: 'Billing or payout method verified',
    className: 'text-sky-700 bg-sky-50 border-sky-200',
    weight: 20,
  },
  top_rated: {
    icon: IconStarFilled,
    label: 'Top Rated',
    shortLabel: 'Top',
    tooltip: 'Consistently high client ratings over time',
    className: 'text-amber-700 bg-amber-50 border-amber-200',
    weight: 20,
  },
  response_rate: {
    icon: IconBolt,
    label: 'Fast Responder',
    shortLabel: 'Response',
    tooltip: 'Replies to new messages quickly',
    className: 'text-violet-700 bg-violet-50 border-violet-200',
    weight: 15,
  },
  on_time_delivery: {
    icon: IconClock,
    label: 'On-Time Delivery',
    shortLabel: 'On-time',
    tooltip: 'Delivers milestones on or ahead of schedule',
    className: 'text-indigo-700 bg-indigo-50 border-indigo-200',
    weight: 20,
  },
  // Legacy signals preserved so existing cards keep rendering while we
  // migrate their data to the unified layer.
  vetted: {
    icon: IconUserCheck,
    label: 'Vetted',
    shortLabel: 'Vetted',
    tooltip: 'Passed platform vetting process',
    className: 'text-slate-50 bg-slate-900 border-slate-900',
    weight: 20,
  },
  new_talent: {
    icon: IconSparkles,
    label: 'New Talent',
    shortLabel: 'New',
    tooltip: 'Recently joined the platform',
    className: 'text-slate-700 bg-slate-100 border-slate-200',
    weight: 0,
  },
};

// ---------------------------------------------------------------------------
// Signal derivation
// ---------------------------------------------------------------------------

/**
 * Turn a heterogeneous user/freelancer/applicant object into the concrete set
 * of trust signals we want to surface. We tolerate a wide variety of field
 * shapes because different pages (admin, marketplace, messaging) hand us
 * different payloads.
 */
export const deriveTrustSignals = (user = {}) => {
  if (!user || typeof user !== 'object') return [];

  const signals = [];

  // ---- ID verified (KYC) ----
  const idVerified =
    user.id_verified ||
    user.kyc_verified ||
    user.kycStatus === 'approved' ||
    user.identityVerified ||
    // When a freelancer has just the legacy `verified` boolean we treat it
    // as ID-verified so existing cards keep showing a badge.
    (user.verified === true && user.payment_verified == null);
  if (idVerified) signals.push('id_verified');

  // ---- Payment verified ----
  const paymentVerified =
    user.payment_verified ||
    user.paymentVerified ||
    user.payout_verified ||
    user.billingVerified ||
    user.stripe_connected === true;
  if (paymentVerified) signals.push('payment_verified');

  // ---- Top rated ----
  const rating = parseFloat(user.rating || user.avgRating || 0);
  const totalJobs = Number(
    user.total_jobs || user.total_jobs_completed || user.jobs_completed || 0
  );
  const topRated =
    user.topRated ||
    user.top_rated ||
    (Array.isArray(user.badges) && user.badges.includes('Top Rated')) ||
    (rating >= 4.8 && totalJobs >= 10);
  if (topRated) signals.push('top_rated');

  // ---- Response rate / speed ----
  const responseRate =
    parseFloat(user.response_rate || user.responseRate || 0) || null;
  const responseTime = user.responseTime || user.response_time || '';
  const fastResponder =
    (responseRate !== null && responseRate >= 90) ||
    /<\s*1\s*h|within\s*1\s*h|≈?\s*1\s*h/i.test(String(responseTime));
  if (fastResponder) signals.push('response_rate');

  // ---- On-time delivery ----
  const onTimeRate =
    parseFloat(
      user.on_time_delivery_rate ||
        user.onTimeRate ||
        user.delivery_rate ||
        0
    ) || null;
  const onTime =
    user.on_time_delivery === true ||
    (onTimeRate !== null && onTimeRate >= 95);
  if (onTime) signals.push('on_time_delivery');

  // ---- Legacy: vetted / new talent, only when there is nothing else ----
  if (user.vetted && !signals.includes('id_verified')) {
    signals.push('vetted');
  }
  if (signals.length === 0 && totalJobs === 0) {
    signals.push('new_talent');
  }

  return signals;
};

/**
 * Compute an aggregate 0-100 trust score. This is intentionally simple: the
 * backend may eventually ship its own score, in which case this is a decent
 * client-side fallback so we never show an empty state.
 */
export const computeTrustScore = (user = {}) => {
  if (user.trust_score != null) return Math.round(Number(user.trust_score));
  if (user.trustScore != null) return Math.round(Number(user.trustScore));

  const signals = deriveTrustSignals(user);
  const base = signals.reduce(
    (sum, key) => sum + (TRUST_SIGNALS[key]?.weight || 0),
    0
  );

  // Blend in rating so "new" verified users don't automatically hit 100.
  const rating = parseFloat(user.rating || user.avgRating || 0);
  const ratingBonus = Math.min(rating * 4, 20);

  return Math.min(Math.round(base + ratingBonus), 100);
};

// ---------------------------------------------------------------------------
// Presentational components
// ---------------------------------------------------------------------------

const SIZE_STYLES = {
  xs: 'px-1.5 py-0.5 text-[10px] gap-0.5',
  sm: 'px-2 py-0.5 text-xs gap-1',
  md: 'px-2.5 py-1 text-xs gap-1',
  lg: 'px-3 py-1.5 text-sm gap-1.5',
};

const ICON_SIZES = { xs: 10, sm: 12, md: 14, lg: 16 };

/**
 * A single trust pill.
 *
 *   <TrustBadge type="id_verified" size="sm" />
 */
export const TrustBadge = ({
  type,
  size = 'sm',
  showLabel = true,
  short = false,
  className = '',
}) => {
  const signal = TRUST_SIGNALS[type];
  if (!signal) return null;
  const Icon = signal.icon;
  const label = short ? signal.shortLabel : signal.label;

  return (
    <span
      className={`inline-flex items-center rounded-full border font-medium ${signal.className} ${SIZE_STYLES[size] || SIZE_STYLES.sm} ${className}`}
      title={signal.tooltip}
      aria-label={signal.label}
    >
      <Icon size={ICON_SIZES[size] || 12} aria-hidden="true" />
      {showLabel && <span>{label}</span>}
    </span>
  );
};

/**
 * Renders the full set of derived signals for a user, optionally capped and
 * with a "+N" overflow indicator. Use this on cards, proposal headers, etc.
 *
 *   <TrustBadgeGroup user={freelancer} max={4} />
 */
export const TrustBadgeGroup = ({
  user,
  signals,
  max = 4,
  size = 'sm',
  short = false,
  showLabel = true,
  className = '',
}) => {
  const list = signals || deriveTrustSignals(user);
  if (!list || list.length === 0) return null;

  const visible = list.slice(0, max);
  const overflow = list.length - visible.length;

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`}>
      {visible.map((type) => (
        <TrustBadge
          key={type}
          type={type}
          size={size}
          short={short}
          showLabel={showLabel}
        />
      ))}
      {overflow > 0 && (
        <span className="text-[10px] font-medium text-slate-500">
          +{overflow}
        </span>
      )}
    </div>
  );
};

/**
 * Compact "single-glance" trust score chip. Useful in freelancer cards,
 * proposal headers, chat lists, etc.
 *
 *   <TrustScore user={freelancer} />
 */
export const TrustScore = ({
  user,
  score,
  size = 'sm',
  showLabel = true,
  className = '',
}) => {
  const value = score != null ? score : computeTrustScore(user);
  const tier =
    value >= 85
      ? { label: 'Excellent', color: 'text-emerald-700 bg-emerald-50 border-emerald-200' }
      : value >= 70
        ? { label: 'Trusted', color: 'text-sky-700 bg-sky-50 border-sky-200' }
        : value >= 50
          ? { label: 'Building', color: 'text-amber-700 bg-amber-50 border-amber-200' }
          : { label: 'New', color: 'text-slate-600 bg-slate-100 border-slate-200' };

  return (
    <span
      className={`inline-flex items-center rounded-full border font-semibold ${tier.color} ${SIZE_STYLES[size] || SIZE_STYLES.sm} ${className}`}
      title={`Trust score: ${value}/100 (${tier.label})`}
      aria-label={`Trust score ${value} out of 100, ${tier.label}`}
    >
      <IconAward size={ICON_SIZES[size] || 12} aria-hidden="true" />
      <span>{value}</span>
      {showLabel && <span className="opacity-70">{tier.label}</span>}
    </span>
  );
};

export default TrustBadge;
